import {
  OFFLINE_QURAN_CACHE_NAME,
  pinQuranFile,
  getTextPackState,
  markTextPackText,
  markTextPackTranslation,
} from './offlineStore'

/**
 * Background "text pack": pins all 114 surahs' Arabic text plus the active
 * translation edition into the offline quran cache (a few MB total) so
 * READING always works offline. Audio stays an explicit download.
 * Resumable: every file is probed in the cache before fetching. Excluded
 * from the download budget by design.
 */

const SURAH_COUNT = 114
const CONCURRENCY = 3

export interface TextPackResult {
  fetched: number
  skipped: number
  failed: number
}

async function hasCached(url: string): Promise<boolean> {
  if (typeof caches === 'undefined') return false
  try {
    const cache = await caches.open(OFFLINE_QURAN_CACHE_NAME)
    return (await cache.match(url)) !== undefined
  } catch {
    return false
  }
}

async function pinBatch(urls: string[], result: TextPackResult): Promise<boolean> {
  let allOk = true
  let cursor = 0
  while (cursor < urls.length) {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return false
    const slice = urls.slice(cursor, cursor + CONCURRENCY)
    const outcomes = await Promise.all(
      slice.map(async (url) => {
        if (await hasCached(url)) return 'skipped' as const
        return (await pinQuranFile(url)) ? ('fetched' as const) : ('failed' as const)
      }),
    )
    for (const o of outcomes) {
      result[o]++
      if (o === 'failed') allOk = false
    }
    cursor += CONCURRENCY
  }
  return allOk
}

export async function ensureTextPack({ edition }: { edition: string }): Promise<TextPackResult> {
  const result: TextPackResult = { fetched: 0, skipped: 0, failed: 0 }
  const state = getTextPackState()

  const textDone = state.textCompletedAt !== null
  const translationDone = Boolean(state.translations[edition])
  if (textDone && translationDone) {
    result.skipped = SURAH_COUNT * 2
    return result
  }

  if (!textDone) {
    const textUrls = Array.from({ length: SURAH_COUNT }, (_, i) => `/quran/text/${i + 1}.json`)
    if (await pinBatch(textUrls, result)) markTextPackText()
  } else {
    result.skipped += SURAH_COUNT
  }

  if (!translationDone) {
    const trUrls = Array.from({ length: SURAH_COUNT }, (_, i) => `/quran/translations/${edition}/${i + 1}.json`)
    if (await pinBatch(trUrls, result)) markTextPackTranslation(edition)
  } else {
    result.skipped += SURAH_COUNT
  }

  return result
}

// Single-flight guard so route changes / edition flips don't stack runs.
let inFlight: Promise<TextPackResult> | null = null

export function scheduleTextPack(edition: string): void {
  if (inFlight) return
  inFlight = ensureTextPack({ edition }).finally(() => { inFlight = null })
  void inFlight
}
